import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Edit, RefreshCw, UserCheck, AlertTriangle, Trash2 } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import { useEquipes, useCreateEquipe, useUpdateEquipe, useDeleteEquipe } from '@/hooks/useTransport';
import { STATUT_EQUIPE_LABELS, type StatutEquipe, type Equipe } from '@/types';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';

const STATUT_BADGE: Record<StatutEquipe, string> = {
  actif:   'ds-bd ds-bd-s',
  inactif: 'ds-bd ds-bd-n',
};

export const EquipesPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]         = useState<StatutEquipe | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]       = useState<Equipe | undefined>();
  const [error, setError]           = useState<string | null>(null);

  const { data, isLoading, refetch } = useEquipes({ statut: statut || undefined, limit: 100 });
  const equipes = data?.data ?? [];
  const remove  = useDeleteEquipe();

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit   = (e: Equipe) => { setEditing(e); setDialogOpen(true); };

  const onRemove = async (id: string, code: string) => {
    if (!(await demander.confirmer({ titre: `Supprimer l'équipe "${code}" ?`, confirmer: 'Supprimer', danger: true }))) return;
    setError(null);
    try { await remove.mutateAsync(id); }
    catch (err) { setError(extractError(err).message); }
  };

  return (
    <>
      <Topbar
        title="Équipes transport"
        subtitle={filiale ? `${equipes.length} équipe(s) · ${filiale.fullName}` : 'Équipes'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={openCreate}>
              <Plus size={14} /> Nouvelle équipe
            </button>
          </>
        }
      />

      <div className="ds-content">
        {error && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{error}</div>
          </div>
        )}

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutEquipe | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_EQUIPE_LABELS) as StatutEquipe[]).map((s) => (
              <option key={s} value={s}>{STATUT_EQUIPE_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Équipes</div>
              <div className="ds-table-sub">{equipes.length} équipe{equipes.length > 1 ? 's' : ''}</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 80 }}>Code</th>
                <th>Nom</th>
                <th>Chef</th>
                <th>Secteur</th>
                <th>Exploitant</th>
                <th className="ds-am" style={{ width: 90 }}>Camions</th>
                <th style={{ width: 80 }}>Statut</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>
              )}
              {!isLoading && equipes.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>
                    <UserCheck size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                    <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune équipe</p>
                    <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={openCreate}>
                      <Plus size={12} /> Créer la première équipe
                    </button>
                  </td>
                </tr>
              )}
              {!isLoading && equipes.map((e) => (
                <tr key={e.id}>
                  <td><span className="ds-ref-code">{e.code}</span></td>
                  <td><div className="ds-entry-label">{e.nom}</div></td>
                  <td style={{ fontSize: 12 }}>{e.chefNom ?? '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--tx-2)' }}>{e.secteur ?? '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--tx-2)' }}>{e.exploitant ?? '—'}</td>
                  <td className="ds-am" style={{ fontWeight: 600 }}>{e._count?.camions ?? 0}</td>
                  <td><span className={STATUT_BADGE[e.statut]}>{STATUT_EQUIPE_LABELS[e.statut]}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => openEdit(e)} className="ds-row-btn" title="Modifier">
                        <Edit size={13} />
                      </button>
                      <button onClick={() => onRemove(e.id, e.code)} className="ds-row-btn" title="Supprimer" disabled={remove.isPending}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <EquipeFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} equipe={editing} />
    </>
  );
};

const EquipeFormDialog = ({ open, onClose, equipe }: { open: boolean; onClose: () => void; equipe?: Equipe }) => {
  const isEdit = !!equipe;
  const [code, setCode]             = useState('');
  const [nom, setNom]               = useState('');
  const [chefNom, setChefNom]       = useState('');
  const [secteur, setSecteur]       = useState('');
  const [exploitant, setExploitant] = useState('');
  const [statut, setStatut]         = useState<StatutEquipe>('actif');
  const [notes, setNotes]           = useState('');
  const [error, setError]           = useState<string | null>(null);

  const create = useCreateEquipe();
  const update = useUpdateEquipe();

  useEffect(() => {
    if (!open) return;
    if (equipe) {
      setCode(equipe.code); setNom(equipe.nom);
      setChefNom(equipe.chefNom ?? ''); setSecteur(equipe.secteur ?? '');
      setExploitant(equipe.exploitant ?? ''); setStatut(equipe.statut);
      setNotes(equipe.notes ?? '');
    } else {
      setCode(''); setNom(''); setChefNom(''); setSecteur(''); setExploitant('');
      setStatut('actif'); setNotes('');
    }
    setError(null);
  }, [open, equipe]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        code: code.trim(), nom: nom.trim(),
        chefNom:    chefNom.trim()    || undefined,
        secteur:    secteur.trim()    || undefined,
        exploitant: exploitant.trim() || undefined,
        statut, notes: notes.trim() || undefined,
      };
      if (isEdit && equipe) {
        await update.mutateAsync({ id: equipe.id, payload });
      } else {
        await create.mutateAsync(payload);
      }
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const canSubmit = code.trim() && nom.trim();

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? `Modifier — ${equipe?.code}` : 'Nouvelle équipe'} subtitle="1 chef = 1 équipe = 10 camions max" size="md">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code *">
            <input value={code} onChange={(e) => setCode(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="E-01" />
          </Field>
          <Field label="Statut">
            <select value={statut} onChange={(e) => setStatut(e.target.value as StatutEquipe)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(STATUT_EQUIPE_LABELS) as StatutEquipe[]).map((s) => (
                <option key={s} value={s}>{STATUT_EQUIPE_LABELS[s]}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Nom *">
          <input value={nom} onChange={(e) => setNom(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="Équipe Tongon Nord" />
        </Field>

        <Field label="Chef d'équipe">
          <input value={chefNom} onChange={(e) => setChefNom(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Koné Mamadou" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Secteur">
            <input value={secteur} onChange={(e) => setSecteur(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Mine Tongon" />
          </Field>
          <Field label="Exploitant">
            <input value={exploitant} onChange={(e) => setExploitant(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Barrick Gold" />
          </Field>
        </div>

        <Field label="Notes">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} />
        </Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>
            {isEdit ? 'Enregistrer' : 'Créer l\'équipe'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
